import type fr from '../fr/client';

const en: typeof fr = {
  nav: { title: 'My account', home: 'Home', book: 'Book', properties: 'My properties', calendar: 'Calendar', quotes: 'Quotes', payments: 'Payments', messages: 'Messages', account: 'Account' },
  form: {
    required: 'This field is required.',
    invalid: 'Please check this value.',
  },
  errors: {
    CUTOFF_PASSED: 'The change deadline has passed. Write to the team for any request.',
    JOB_CANCELLED: 'This cleaning was cancelled: no payment is required.',
    AGREEMENT_NOT_ACTIVE: 'This service is not active at the moment.',
    AGREEMENT_NOT_PAUSED: 'This service is not paused.',
    AGREEMENT_CLOSED: 'This service has already ended or been cancelled.',
    AGREEMENT_BILLED_PER_VISIT: 'This service is paid after each visit.',
    STAFF_ONLY_ACTION: 'This change must be made by the team. Please write to us.',
    INVALID_TRANSITION: 'This action is no longer possible. Please refresh the page.',
  },
  dashboard: {
    title: 'Hello {{name}}',
    subtitle: 'Your cleanings, quotes and messages in one place.',
    book: 'Book a cleaning',
    nextTitle: 'Next cleaning',
    emptyTitle: 'You have no cleaning scheduled.',
    emptyDescription: 'Request a cleaning in a few steps: we will send you the official price quickly.',
    emptyNoProperty: 'Add your address, then request a cleaning in a few steps.',
    requestCleaning: 'Request a cleaning',
    upcoming: 'Upcoming cleanings',
    history: 'Recent cleanings',
    openQuotes: 'Open quotes',
    noOpenQuotes: 'No open quotes.',
    properties_zero: 'No property',
    properties_one: '{{count}} property',
    properties_other: '{{count}} properties',
    manageProperties: 'Manage my properties',
    addProperty: 'Add a property',
    toAccept_one: 'Quote to accept',
    toAccept_other: '{{count}} quotes to accept',
    review: 'See the price',
    messages: 'Messages',
    unread_one: '{{count}} unread message',
    unread_other: '{{count}} unread messages',
    noUnread: 'Write to us anytime.',
  },
  job: {
    timeAt: 'at {{time}}',
    number: 'Cleaning #{{number}}',
    crew: 'With {{names}}',
    crewLabel: 'Crew',
    crewPending: 'The crew will be confirmed soon.',
    recurring: 'Recurring',
    messageSubject: 'Cleaning #{{number}} on {{date}}',
    when: 'Date and time',
    duration: 'about {{duration}}',
    seeProperty: 'See property',
    price: 'Price of this cleaning',
    partOfRecurring: 'Part of your recurring service.',
    paymentDue: 'Amount due: {{amount}}',
    instructions: 'Instructions for this cleaning',
    instructionsExplain: 'Apply to this visit only. Permanent instructions are in the property details.',
    instructionsTitle: 'Instructions for this cleaning',
    instructionsHint: 'E.g. focus on the kitchen, guests tonight… These instructions apply to this visit only.',
    instructionsSaved: 'Instructions saved.',
    noInstructions: 'No special instructions for this visit.',
    reschedule: 'Change date or time',
    rescheduleTitle: 'Change date or time',
    rescheduleHint: 'Pick a new time. The team will confirm availability.',
    rescheduleCrewNote: 'The planned crew may not be available at the new time: we will confirm who is coming.',
    rescheduleSubmit: 'Confirm change',
    rescheduled: 'Your cleaning was moved.',
    cancel: 'Cancel this cleaning',
    cancelTitle: 'Cancel this cleaning?',
    cancelDescription: 'The cleaning on {{date}} at {{time}} will be cancelled and the team notified.',
    cancelConfirm: 'Cancel cleaning',
    keep: 'Keep',
    cancelReason: 'Reason for cancelling',
    cancelReasonHint: 'A few words are enough.',
    cancelReasonRequired: 'Please give a reason (at least 2 characters).',
    cancelled: 'The cleaning was cancelled.',
    cutoffRuleHours_one: '{{count}} hour',
    cutoffRuleHours_other: '{{count}} hours',
    cutoffRuleBusinessDays_one: '{{count}} business day',
    cutoffRuleBusinessDays_other: '{{count}} business days',
    cutoffOpen: 'You can change or cancel online until: {{deadline}}.',
    cutoffLockedTitle: 'The {{rule}} change deadline has passed.',
    cutoffLockedBody: 'Deadline: {{deadline}}. For any change or cancellation, write to the team: we will do our best.',
    writeTeam: 'Write to the team',
  },
  book: {
    title: 'Request a cleaning',
    subtitle: 'A few simple questions. The official price is confirmed by our team.',
    progress: 'Progress',
    stepOf: 'Step {{step}} of {{total}}',
    steps: {
      property: 'Property',
      service: 'Cleaning type',
      details: 'Your home',
      when: 'Date and time',
      recurrence: 'Frequency',
      requests: 'Special requests',
      summary: 'Summary',
    },
    stepHints: {
      property: 'Where will the cleaning take place?',
      service: 'Which type of cleaning would you like?',
      details: 'Check your home details: they are used to prepare the price.',
      when: 'Choose the time that suits you.',
      recurrence: 'Just once or on a regular basis?',
      requests: 'Add extras or details for this visit.',
      summary: 'Review your request before sending it.',
    },
    noProperty: 'Add your address first.',
    noPropertyHint: 'A property is needed to request a cleaning.',
    otherProperty: 'Another address',
    detailsHint: 'Changes will also be saved on your property.',
    date: 'Date',
    time: 'Arrival time',
    hours: 'Between {{from}} and {{to}}',
    workingDays: 'Service days: {{days}}',
    whenPreview: 'Cleaning requested on {{date}} at {{time}}.',
    whenValue: '{{date}} at {{time}}',
    whenNote: 'The date is a preference: the team confirms it with the price.',
    frequency: 'Frequency',
    frequencyHint: {
      ONE_TIME: 'A one-off cleaning.',
      WEEKLY: 'Same day every week.',
      BIWEEKLY: 'Every other week.',
      MONTHLY: 'Once a month.',
      CUSTOM: 'You choose the interval.',
    },
    interval: 'Every how many days?',
    intervalHint: 'Between 1 and 120 days.',
    everyNDays_one: 'Every day',
    everyNDays_other: 'Every {{count}} days',
    contract: 'Commitment',
    contractHint: 'A contract is optional. The team will present the terms with the price.',
    extras: 'Extras',
    extrasHint: 'Check what you would like to add to this visit.',
    specialRequests: 'Special requests',
    specialRequestsHint: 'E.g. focus on the bathroom, do not move the plants…',
    noRequests: 'No special requests.',
    estimateTitle: 'Indicative price',
    estimateRange: 'Between {{low}} and {{high}}',
    estimateDuration: 'Estimated duration: about {{duration}}',
    estimateUnavailable: 'The indicative price is not available right now. You can still send your request.',
    estimateNote: 'This amount is an estimate. The official price is confirmed by our team before any cleaning: you then accept or decline it.',
    submit: 'Send my request',
    submitted: 'Request sent! We will send you the official price shortly.',
    errors: {
      property: 'Choose a property.',
      service: 'Choose a cleaning type.',
      details: 'Your home details are not loaded yet.',
      squareFeet: 'The area must be between 100 and 100,000 sq ft.',
      date: 'Choose a date from tomorrow on.',
      time: 'Choose a time.',
      closedDay: 'We do not clean on that day. Please choose another day.',
      interval: 'Enter an interval between 1 and 120 days.',
    },
  },
  properties: {
    title: 'My properties',
    subtitle: 'Your addresses and the information used at every visit.',
    add: 'Add a property',
    inactive: 'Inactive',
    bedBath: '{{bedrooms}} bd · {{bathrooms}} ba',
    pets: 'Pets',
    view: 'View profile',
    noHistory: 'No past cleaning at this address.',
    noPreferences: 'No preferences saved.',
    tabs: { info: 'Information', instructions: 'Instructions', preferences: 'Preferences', access: 'Access', history: 'History' },
    chips: {
      bedrooms_one: '{{count}} bedroom',
      bedrooms_other: '{{count}} bedrooms',
      bathrooms_one: '{{count}} bathroom',
      bathrooms_other: '{{count}} bathrooms',
      floors_one: '{{count}} floor',
      floors_other: '{{count}} floors',
      parking: 'Parking',
    },
    emptyTitle: 'No property yet.',
    emptyDescription: 'Add your address to request a cleaning.',
    newTitle: 'New property',
    editTitle: 'Edit property',
    formSubtitle: 'This information is permanent: it applies to every cleaning.',
    create: 'Add property',
    created: 'Property added.',
    saved: 'Property saved.',
    bookHere: 'Book here',
    map: 'Map',
    roomsSummary: '{{bedrooms}} bd · {{bathrooms}} ba · {{floors}} floor(s)',
    kitchensFamily: '{{kitchens}} kitchen(s) · {{familyRooms}} living room(s)',
    sqft: '{{value}} sq ft',
    permanentExplain: 'Apply to every visit. Update them when your habits change.',
    perVisitExplain: 'For a request that applies to one cleaning only (guests, priority room…), add it when booking or in that cleaning’s details.',
    noPermanent: 'No permanent instructions.',
    addPermanent: 'Add some',
    teamNotes: 'Notes from the team',
    noTeamNotes: 'No notes from the team for this property.',
    sections: {
      address: 'Address',
      characteristics: 'Characteristics',
      permanent: 'Permanent instructions',
      permanentHint: 'Valid for every visit. Instructions for a single cleaning are given when booking.',
    },
    fields: {
      name: 'Property name',
      nameHint: 'E.g. House, Cottage, Downtown condo',
      addressLine1: 'Address',
      unit: 'Apartment / unit',
      city: 'City',
      province: 'Province',
      postalCode: 'Postal code',
      propertyType: 'Home type',
      floors: 'Floors',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      kitchens: 'Kitchens',
      familyRooms: 'Living / family rooms',
      squareFeet: 'Area (sq ft)',
      hasPets: 'Pets at home',
      petDetails: 'Pet details',
      parkingInfo: 'Parking',
      permanentNotes: 'General instructions',
      permanentNotesHint: 'What the team should know at every visit.',
      preferences: 'Preferences',
      specialProducts: 'Special products',
      forbiddenAreas: 'Rooms or areas to avoid',
    },
  },
  access: {
    title: 'Home access',
    privacy: 'This information is protected: employees only see it around their visit.',
    empty: 'No access information. Add a code, key or alarm if the team needs to get in without you.',
    addTitle: 'Add access information',
    editTitle: 'Edit access information',
    dialogHint: 'Permanent information, used at every visit.',
    kind: 'Type',
    label: 'Description',
    labelHint: 'E.g. Front door, Lockbox by the garage',
    secret: 'Code or value',
    secretHint: 'Optional. Only visible to you and the authorized team.',
    notes: 'Details',
    show: 'Show code',
    hide: 'Hide code',
    added: 'Access information added.',
    updated: 'Access information updated.',
    deleted: 'Access information deleted.',
    deleteTitle: 'Delete this access information?',
    deleteDescription: '“{{label}}” will be deleted. The team will no longer see it.',
  },
  calendar: {
    title: 'Calendar',
    subtitle: 'Your upcoming cleanings and history.',
    upcoming: 'Upcoming',
    history: 'History',
    historyEmpty: 'No past cleaning yet.',
  },
  quotes: {
    title: 'Quotes',
    subtitle: 'Your price requests and our answers.',
    number: 'Quote #{{number}}',
    requestedOn: 'Requested on {{date}}',
    requestedFor: 'Requested for {{date}} at {{time}}',
    emptyTitle: 'No quotes yet.',
    emptyDescription: 'Request a cleaning: we will send you the official price.',
  },
  quote: {
    priceTitle: 'Our price',
    discount: 'Discount',
    total: 'Total',
    totalPerVisit: 'Total per visit',
    duration_one: 'Estimated duration: {{duration}} · {{count}} person',
    duration_other: 'Estimated duration: {{duration}} · {{count}} people',
    validUntil: 'Price valid until {{date}}.',
    preparingTitle: 'Our team is preparing your price.',
    preparingBody: 'You will be notified as soon as it is ready. You can then accept or decline it.',
    cancelledTitle: 'Request cancelled.',
    cancelledBody: 'This request is no longer active. You can make a new one at any time.',
    yourNote: 'Your comment',
    requestTitle: 'Your request',
    requestedDate: 'Requested date',
    sentOn: 'Sent on',
    answerHint: 'Does the price suit you?',
    accept: 'Accept',
    reject: 'Decline',
    acceptTitle: 'Accept this price?',
    acceptDescription: 'You accept the price of {{amount}}. The cleaning will be scheduled and the team notified.',
    acceptDescriptionRecurring: 'You accept the price of {{amount}} per visit. Your cleanings will be scheduled at the chosen frequency.',
    acceptNote: 'A message for the team',
    rejectTitle: 'Decline this price?',
    rejectDescription: 'The team will be notified. Tell us why if you wish: we may be able to offer something else.',
    rejectNote: 'Reason for declining',
    acceptedToast: 'Price accepted. Thank you!',
    rejectedToast: 'Your answer was sent.',
    acceptedTitle: 'It’s confirmed!',
    acceptedBody: 'Your first cleaning is scheduled on {{date}} at {{time}}.',
    acceptedBodyNoJob: 'The team will schedule your cleaning and confirm the date.',
    seeFirstJob: 'See the first cleaning',
    ask: 'Ask a question',
    askSubject: 'Question about quote #{{number}}',
    cancelRequest: 'Cancel request',
    cancelRequestTitle: 'Cancel this request?',
    cancelRequestDescription: 'The team will not prepare a price for this request.',
    cancelledToast: 'Request cancelled.',
  },
  payments: {
    title: 'Payments',
    subtitle: 'Amounts due and your receipts.',
    due: 'Due',
    paid: 'Paid',
    toPay: 'To pay',
    historyTitle: 'History',
    pay: 'Pay',
    retry: 'Retry payment',
    cleaning: 'Cleaning #{{number}}',
    payment: 'Payment',
    jobOn: 'Cleaning on {{date}}',
    paidOn: 'paid on {{date}}',
    refunded: 'Refunded: {{amount}}',
    seeCleaning: 'See cleaning',
    failedHint: 'The payment did not go through. You can try again with the same card or another one.',
    contactToPay: 'Write to the team to settle this amount.',
    emptyTitle: 'No payments yet.',
    emptyDescription: 'Amounts due will appear here after your cleanings.',
    successToast: 'Payment received. Thank you!',
    cancelledToast: 'Payment cancelled.',
    successBanner: 'Thank you! Your payment was received. It may take a few seconds to appear below.',
    cancelledBanner: 'The payment was cancelled. Nothing was charged.',
  },
  messages: {
    title: 'Messages',
    subtitle: 'Write to the team: we answer quickly.',
    new: 'New message',
    newTitle: 'Write to the team',
    newDescription: 'Your message goes to our customer service team.',
    subject: 'Subject',
    body: 'Message',
    send: 'Send',
    pick: 'Choose a conversation to display it.',
    relatedJob: 'See cleaning',
    relatedQuote: 'See quote',
    archived: 'This conversation is archived. Write a new message if needed.',
  },
  account: {
    title: 'My account',
    subtitle: 'Your contact details, language and recurring services.',
    profile: 'Contact details',
    displayName: 'Name',
    emailHint: 'To change your email, write to the team.',
    secondaryPhone: 'Other phone',
    saved: 'Contact details saved.',
    language: 'Language',
    languageHint: 'Language of the app and of your emails.',
    languageSaved: 'Language saved.',
    languageMismatch: 'Your emails are sent in the other language. Choose a language to save it.',
  },
  agreements: {
    title: 'My recurring services',
    subtitle: 'Pause, resume or cancel a service.',
    emptyTitle: 'No recurring service.',
    emptyDescription: 'Choose a frequency when requesting a cleaning for regular service.',
    since: 'Since {{date}}',
    until: 'Until {{date}}',
    pausedRange: 'Paused from {{from}} to {{until}}.',
    pausedOpen: 'Paused since {{from}}.',
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel service',
    pauseTitle: 'Pause the service',
    pauseDescription: 'Cleanings scheduled during the pause will be cancelled. You can resume at any time.',
    pauseFrom: 'From',
    pauseUntil: 'Until',
    pauseUntilHint: 'Optional: leave empty to resume yourself later.',
    pauseUntilError: 'The end must be after the start.',
    paused: 'Service paused.',
    resumeTitle: 'Resume this service?',
    resumeDescription: 'Upcoming cleanings will be scheduled again at the planned frequency.',
    resumed: 'Service resumed.',
    cancelTitle: 'Cancel this recurring service?',
    cancelDescription: 'Future cleanings of this service will be cancelled. Past cleanings and payments stay in your history.',
    cancelConfirm: 'Cancel service',
    cancelReason: 'Reason for cancelling',
    cancelled: 'Service cancelled.',
  },
};
export default en;
